import { useContext, useMemo } from 'react';
import { useRBAC } from '../context/RBACContext';
import { useOrganization } from '../context/OrganizationContext';
import { 
  PermissionString, 
  PermissionChecker, 
  PermissionContext,
  PermissionUtils,
  PermissionDebug,
  PERMISSIONS
} from '../utils/permissions';

/**
 * Custom hook for permission-based UI control
 * Provides a clean, declarative API for checking user permissions
 */
export const usePermissions = () => {
  const { userPermissions } = useRBAC();
  const { currentOrganizationId, currentTeamId } = useOrganization();

  // Create permission context
  const context: PermissionContext = useMemo(() => ({
    organizationId: currentOrganizationId || undefined,
    teamId: currentTeamId || undefined,
  }), [currentOrganizationId, currentTeamId]);

  // Extract permission strings from Permission objects
  const permissionStrings = useMemo(() => {
    return userPermissions.map(p => {
      // Handle both string format and Permission object format
      if (typeof p === 'string') {
        return p;
      }
      // If it's a Permission object, construct the permission string
      return `${p.resource}:${p.action}`;
    });
  }, [userPermissions]);

  // Create permission checker instance
  const checker = useMemo(() => 
    new PermissionChecker(permissionStrings), 
    [permissionStrings]
  );

  // Basic permission checking functions
  const hasPermission = (permission: PermissionString): boolean => {
    return checker.hasPermission(permission);
  };

  const hasAllPermissions = (permissions: PermissionString[]): boolean => {
    return checker.hasAllPermissions(permissions);
  };

  const hasAnyPermission = (permissions: PermissionString[]): boolean => {
    return checker.hasAnyPermission(permissions);
  };

  // Semantic permission checking functions using PermissionUtils
  const canViewInvitations = (): boolean => {
    return PermissionUtils.canViewInvitations(checker);
  };

  const canInviteMembers = (): boolean => {
    return PermissionUtils.canInviteMembers(checker);
  };

  const canManageTeams = (): boolean => {
    return PermissionUtils.canManageTeams(checker);
  };

  const canManageOrganization = (): boolean => {
    return PermissionUtils.canManageOrganization(checker);
  };

  const canManageRoles = (): boolean => {
    return PermissionUtils.canManageRoles(checker);
  };

  const canViewOrganizationSettings = (): boolean => {
    return PermissionUtils.canViewOrganizationSettings(checker);
  };

  // Subscription permission checking functions
  const canViewSubscriptions = (): boolean => {
    return PermissionUtils.canViewSubscriptions(checker);
  };

  const canCreateSubscriptions = (): boolean => {
    return PermissionUtils.canCreateSubscriptions(checker);
  };

  const canUpdateSubscriptions = (): boolean => {
    return PermissionUtils.canUpdateSubscriptions(checker);
  };

  const canCancelSubscriptions = (): boolean => {
    return PermissionUtils.canCancelSubscriptions(checker);
  };

  const canReactivateSubscriptions = (): boolean => {
    return PermissionUtils.canReactivateSubscriptions(checker);
  };

  const canDeleteSubscriptions = (): boolean => {
    return PermissionUtils.canDeleteSubscriptions(checker);
  };

  const canAssignTeamsToSubscriptions = (): boolean => {
    return PermissionUtils.canAssignTeamsToSubscriptions(checker);
  };

  const canManageSubscriptionAddOns = (): boolean => {
    return PermissionUtils.canManageSubscriptionAddOns(checker);
  };

  const canManageSubscriptions = (): boolean => {
    return PermissionUtils.canManageSubscriptions(checker);
  };

  // Payment method permission checking functions
  const canViewPaymentMethods = (): boolean => {
    return PermissionUtils.canViewPaymentMethods(checker);
  };

  const canAddPaymentMethods = (): boolean => {
    return PermissionUtils.canAddPaymentMethods(checker);
  };

  const canUpdatePaymentMethods = (): boolean => {
    return PermissionUtils.canUpdatePaymentMethods(checker);
  };

  const canDeletePaymentMethods = (): boolean => {
    return PermissionUtils.canDeletePaymentMethods(checker);
  };

  const canManagePaymentMethods = (): boolean => {
    return PermissionUtils.canManagePaymentMethods(checker);
  };

  // Debug functions (only in development)
  const debugPermissions = () => {
    if (process.env.NODE_ENV === 'development') {
      PermissionDebug.logPermissions(permissionStrings, context);
    }
  };

  const debugPermissionCheck = (permission: PermissionString, description?: string) => {
    if (process.env.NODE_ENV === 'development') {
      return PermissionDebug.debugPermissionCheck(checker, permission, description);
    }
    return checker.hasPermission(permission);
  };

  return {
    // Context information
    context,
    permissions: permissionStrings,

    // Basic permission checking
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,

    // Semantic permission checking - General
    canViewInvitations,
    canInviteMembers,
    canManageTeams,
    canManageOrganization,
    canManageRoles,
    canViewOrganizationSettings,

    // Semantic permission checking - Subscriptions
    canViewSubscriptions,
    canCreateSubscriptions,
    canUpdateSubscriptions,
    canCancelSubscriptions,
    canReactivateSubscriptions,
    canDeleteSubscriptions,
    canAssignTeamsToSubscriptions,
    canManageSubscriptionAddOns,
    canManageSubscriptions,

    // Semantic permission checking - Payment Methods
    canViewPaymentMethods,
    canAddPaymentMethods,
    canUpdatePaymentMethods,
    canDeletePaymentMethods,
    canManagePaymentMethods,

    // Utility functions
    getResourcePermissions: (resource: string) => checker.getResourcePermissions(resource),
    getAllPermissions: () => checker.getAllPermissions(),

    // Debug functions
    debugPermissions,
    debugPermissionCheck,

    // Raw checker for advanced use cases
    checker,
  };
};

/**
 * Hook for organization-specific permissions
 * Automatically filters permissions for the current organization context
 */
export const useOrganizationPermissions = (organizationId?: string) => {
  const permissions = usePermissions();
  const targetOrgId = organizationId || permissions.context.organizationId;

  // In the future, this could filter permissions based on organization
  // For now, it returns the same permissions but provides organization context
  return {
    ...permissions,
    organizationId: targetOrgId,
    // Organization-specific semantic checks
    canDeleteOrganization: () => permissions.hasPermission(PERMISSIONS.ORGANIZATION_DELETE),
    canUpdateOrganization: () => permissions.hasPermission(PERMISSIONS.ORGANIZATION_UPDATE),
    canCreateTeams: () => permissions.hasPermission(PERMISSIONS.TEAM_CREATE),
  };
};

/**
 * Hook for team-specific permissions
 * Automatically filters permissions for the current team context
 */
export const useTeamPermissions = (teamId?: string) => {
  const permissions = usePermissions();
  const targetTeamId = teamId || permissions.context.teamId;

  return {
    ...permissions,
    teamId: targetTeamId,
    // Team-specific semantic checks
    canDeleteTeam: () => permissions.hasPermission(PERMISSIONS.TEAM_DELETE),
    canUpdateTeam: () => permissions.hasPermission(PERMISSIONS.TEAM_UPDATE),
    canInviteToTeam: () => permissions.hasPermission(PERMISSIONS.TEAM_INVITE),
  };
};

/**
 * Hook that returns permission-based component visibility
 * Useful for conditionally rendering entire components
 */
export const usePermissionVisibility = () => {
  const permissions = usePermissions();
  const { currentOrganizationId } = useOrganization();

  // Create organization-only permission checker for organization-level features
  // This is critical for subscription/payment features which are organization-scoped,
  // not team-scoped. Without this, when a team is selected, the permission context
  // includes teamId which incorrectly filters organization-level permissions.
  const orgOnlyContext: PermissionContext = useMemo(() => ({
    organizationId: currentOrganizationId || undefined,
    teamId: undefined,  // Always undefined to check org-level permissions only
  }), [currentOrganizationId]);

  const permissionStrings = useMemo(() => {
    return permissions.permissions;
  }, [permissions.permissions]);

  const orgOnlyChecker = useMemo(() =>
    new PermissionChecker(permissionStrings),
    [permissionStrings]
  );

  return {
    // Tab visibility
    showInvitationsTab: permissions.canViewInvitations(),
    showRolesTab: permissions.canManageRoles(),
    // Subscriptions are organization-level, use org-only checker
    showSubscriptionsTab: PermissionUtils.canViewSubscriptions(orgOnlyChecker),
    showPaymentMethodsTab: PermissionUtils.canViewPaymentMethods(orgOnlyChecker),
    showSettingsTab: permissions.canViewOrganizationSettings(),

    // Button visibility - General
    showInviteButton: permissions.canInviteMembers(),
    showCreateTeamButton: permissions.canManageTeams(),
    showDeleteOrganizationButton: permissions.canManageOrganization(),

    // Button visibility - Subscriptions (all organization-level, use org-only checker)
    showCreateSubscriptionButton: PermissionUtils.canCreateSubscriptions(orgOnlyChecker),
    showCancelSubscriptionButton: PermissionUtils.canCancelSubscriptions(orgOnlyChecker),
    showDeleteSubscriptionButton: PermissionUtils.canDeleteSubscriptions(orgOnlyChecker),
    showEditSubscriptionButton: PermissionUtils.canUpdateSubscriptions(orgOnlyChecker),
    showManageAddOnsButton: PermissionUtils.canManageSubscriptionAddOns(orgOnlyChecker),
    showAssignTeamsButton: PermissionUtils.canAssignTeamsToSubscriptions(orgOnlyChecker),

    // Button visibility - Payment Methods (organization-level, use org-only checker)
    showAddPaymentMethodButton: PermissionUtils.canAddPaymentMethods(orgOnlyChecker),
    showDeletePaymentMethodButton: PermissionUtils.canDeletePaymentMethods(orgOnlyChecker),
    showSetDefaultPaymentButton: PermissionUtils.canUpdatePaymentMethods(orgOnlyChecker),

    // Section visibility
    showMemberManagement: permissions.hasAnyPermission([
      PERMISSIONS.MEMBER_READ,
      PERMISSIONS.MEMBER_INVITE,
      PERMISSIONS.MEMBER_REMOVE,
    ]),
    showTeamManagement: permissions.hasAnyPermission([
      PERMISSIONS.TEAM_READ,
      PERMISSIONS.TEAM_SETTINGS,
    ]),
    // Subscriptions/payments are organization-level
    showSubscriptionManagement: PermissionUtils.canViewSubscriptions(orgOnlyChecker),
    showPaymentMethodManagement: PermissionUtils.canViewPaymentMethods(orgOnlyChecker),
  };
};

export default usePermissions;